import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "@/pages/AdminDashboard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Save, MessageSquare, Mail, Eye, EyeOff, TestTube, Phone, CheckCircle } from "lucide-react";

export default function AdminSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  const [smsSettings, setSmsSettings] = useState({
    provider: "netgsm",
    usercode: "",
    password: "",
    msgheader: "",
    phone: "",
  });

  const [emailSettings, setEmailSettings] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_password: "",
    from_email: "",
    from_name: "Salon Bahadır Kersü",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.sms) setSmsSettings(prev => ({ ...prev, ...res.data.sms }));
        if (res.data.email) setEmailSettings(prev => ({ ...prev, ...res.data.email }));
      } catch (error) {
        // Settings might not exist yet
      }
    };
    fetchSettings();
  }, [token]);

  const handleSaveSMS = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings/sms`, smsSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("SMS ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings/email`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("E-posta ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleTestSMS = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/admin/settings/test-sms`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test SMS gönderildi");
    } catch (error) {
      toast.error("Test SMS gönderilemedi");
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/admin/settings/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test e-posta gönderildi");
    } catch (error) {
      toast.error("Test e-posta gönderilemedi");
    } finally {
      setTestLoading(false);
    }
  };

  const togglePassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <AdminLayout title="Ayarlar">
      <div className="max-w-3xl space-y-8">

        {/* SMS Settings */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-display text-lg text-[#1a1a2e] font-semibold">SMS Ayarları (NetGSM)</h2>
              <p className="text-sm text-[#6b7280]">NetGSM hesap bilgilerinizi girin</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Kullanıcı Kodu</label>
              <Input
                placeholder="NetGSM kullanıcı kodu"
                value={smsSettings.usercode}
                onChange={(e) => setSmsSettings(prev => ({ ...prev, usercode: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Şifre</label>
              <div className="relative">
                <Input
                  type={showPasswords.sms ? "text" : "password"}
                  placeholder="NetGSM şifresi"
                  value={smsSettings.password}
                  onChange={(e) => setSmsSettings(prev => ({ ...prev, password: e.target.value }))}
                  className="input-elite pr-10"
                />
                <button
                  type="button"
                  onClick={() => togglePassword("sms")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#6b7280]"
                >
                  {showPasswords.sms ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Mesaj Başlığı</label>
              <Input
                placeholder="SMS gönderici adı"
                value={smsSettings.msgheader}
                onChange={(e) => setSmsSettings(prev => ({ ...prev, msgheader: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Yönetici Telefonu</label>
              <Input
                placeholder="+90 5XX XXX XX XX"
                value={smsSettings.phone}
                onChange={(e) => setSmsSettings(prev => ({ ...prev, phone: e.target.value }))}
                className="input-elite"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button onClick={handleSaveSMS} className="btn-primary" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
            <Button onClick={handleTestSMS} variant="outline" className="border-[#e5e7eb] text-[#6b7280] hover:bg-[#fafafa]" disabled={testLoading}>
              <Phone className="w-4 h-4 mr-2" />
              Test SMS Gönder
            </Button>
          </div>
        </div>

        {/* Email Settings */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-display text-lg text-[#1a1a2e] font-semibold">E-posta Ayarları (SMTP)</h2>
              <p className="text-sm text-[#6b7280]">SMTP sunucu bilgilerinizi girin</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">SMTP Sunucu</label>
              <Input
                placeholder="smtp.gmail.com"
                value={emailSettings.smtp_host}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">SMTP Port</label>
              <Input
                placeholder="587"
                value={emailSettings.smtp_port}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">SMTP Kullanıcı</label>
              <Input
                placeholder="email@gmail.com"
                value={emailSettings.smtp_user}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">SMTP Şifre</label>
              <div className="relative">
                <Input
                  type={showPasswords.email ? "text" : "password"}
                  placeholder="Uygulama şifresi"
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                  className="input-elite pr-10"
                />
                <button
                  type="button"
                  onClick={() => togglePassword("email")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#6b7280]"
                >
                  {showPasswords.email ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Gönderici E-posta</label>
              <Input
                placeholder="info@salonbahadirkersu.com"
                value={emailSettings.from_email}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
                className="input-elite"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Gönderici Adı</label>
              <Input
                placeholder="Salon Bahadır Kersü"
                value={emailSettings.from_name}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, from_name: e.target.value }))}
                className="input-elite"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button onClick={handleSaveEmail} className="btn-primary" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
            <Button onClick={handleTestEmail} variant="outline" className="border-[#e5e7eb] text-[#6b7280] hover:bg-[#fafafa]" disabled={testLoading}>
              <Mail className="w-4 h-4 mr-2" />
              Test E-posta Gönder
            </Button>
          </div>
        </div>

        {/* SMS Preview */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <Eye className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-display text-lg text-[#1a1a2e] font-semibold">SMS Önizlemeleri</h2>
              <p className="text-sm text-[#6b7280]">Her olay için gönderilecek SMS mesajları</p>
            </div>
          </div>

          <div className="space-y-4">
            {[
              {
                event: "Randevu Oluşturuldu",
                to: "Müşteri + Yönetici",
                customerMsg: "Salon Bahadır Kersü: Randevunuz oluşturuldu. Tarih: 15.03.2026, Saat: 14:00, Hizmet: Saç Kesimi, Berber: Ahmet. Onay bekleniyor.",
                adminMsg: "Yeni randevu! Müşteri: Mehmet Yılmaz, Tel: +905551234567, Tarih: 15.03.2026 14:00, Hizmet: Saç Kesimi. Onaylamak için paneli kontrol edin."
              },
              {
                event: "Randevu Onaylandı",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü: Randevunuz onaylandı! Tarih: 15.03.2026, Saat: 14:00, Hizmet: Saç Kesimi, Berber: Ahmet. Sizi bekliyoruz!",
                adminMsg: null
              },
              {
                event: "Randevu Reddedildi",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü: Üzgünüz, randevunuz (15.03.2026 14:00) reddedildi. Lütfen başka bir zaman seçin veya bizi arayın.",
                adminMsg: null
              },
              {
                event: "Randevu İptal Edildi",
                to: "Müşteri + Yönetici",
                customerMsg: "Salon Bahadır Kersü: Randevunuz (15.03.2026 14:00) iptal edildi.",
                adminMsg: "Randevu iptal edildi. Müşteri: Mehmet Yılmaz, Tarih: 15.03.2026 14:00, Hizmet: Saç Kesimi."
              },
              {
                event: "Randevu Tamamlandı",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü: Ziyaretiniz için teşekkür ederiz! Bizi değerlendirmeniz için: [link]. Tekrar görüşmek üzere!",
                adminMsg: null
              },
              {
                event: "24 Saat Hatırlatma",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü hatırlatması: Yarın saat 14:00'te randevunuz var. Hizmet: Saç Kesimi, Berber: Ahmet. Görüşmek üzere!",
                adminMsg: null
              },
              {
                event: "2 Saat Hatırlatma",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü hatırlatması: Randevunuz bugün saat 14:00'te. Hizmet: Saç Kesimi, Berber: Ahmet. Lütfen zamanında gelin.",
                adminMsg: null
              },
              {
                event: "OTP Doğrulama",
                to: "Müşteri",
                customerMsg: "Salon Bahadır Kersü doğrulama kodunuz: 123456. Bu kod 5 dakika geçerlidir.",
                adminMsg: null
              },
            ].map((sms, i) => (
              <div key={i} className="border border-[#f3f4f6] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#1a1a2e]">{sms.event}</span>
                  <span className="text-xs px-2 py-1 bg-[#f5efe6] text-[#c9a96e] rounded-full font-medium">{sms.to}</span>
                </div>
                <div className="space-y-2">
                  <div className="bg-[#fafafa] rounded-lg p-3">
                    <p className="text-xs font-medium text-[#6b7280] mb-1">Müşteriye:</p>
                    <p className="text-sm text-[#1a1a2e]">{sms.customerMsg}</p>
                  </div>
                  {sms.adminMsg && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-600 mb-1">Yöneticiye:</p>
                      <p className="text-sm text-[#1a1a2e]">{sms.adminMsg}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
